import React, { useState } from "react";

import {
  ActionCard,
  CheckForm,
  Divider,
  Grid,
  Group,
  Section,
} from "@samskip/frontend-components";
import { Control, UseFormRegister, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AddressType, BookingTemplateConstants } from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { CommonTypes } from "@assets/types";
import { PartnerCardInfo, PartnerModal } from "@components";
import {
  getNotifiers,
  getPartners,
  partnersState,
} from "@state/slices/companySlice";
import { userProfileState } from "@state/slices/userProfileSlice";

interface AddressesSection {
  control: Control<BookingTemplateFormTypes.RouteForm>;
  onAddressRemove: (
    partnerAddressType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => void;
  onAddressSave: (
    partnerAddressType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE,
    formValues: BookingTemplateFormTypes.PartnerForm
  ) => void;
  onAddressTypeChange: CommonTypes.CallbackFunction;
  register: UseFormRegister<BookingTemplateFormTypes.RouteForm>;
}

const AddressesSection = ({
  control,
  onAddressRemove,
  onAddressSave,
  onAddressTypeChange,
  register,
}: AddressesSection) => {
  const [openedModal, setOpenedModal] =
    useState<BookingTemplateConstants.PARTNER_ADDRESS_TYPE | null>(null);

  const { userProfile } = useAppSelector(userProfileState);
  const { partners, isLoading } = useAppSelector(partnersState);
  const shipper = useWatch({
    control,
    name: "Shipper",
  });
  const consignee = useWatch({
    control,
    name: "Consignee",
  });
  const notifier = useWatch({
    control,
    name: "Notifier",
  });
  const bookingPartyType = useWatch({
    control,
    name: "BookingPartyType",
  });

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const getTitle = (
    openedModalType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => {
    switch (openedModalType) {
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.CONSIGNEE:
        return "LABEL_CONSIGNEE";
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER:
        return "LABEL_NOTIFIER";
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.SHIPPER:
        return "LABEL_SHIPPER";
    }
  };

  const getPartnerValues = (
    openedModalType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => {
    switch (openedModalType) {
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.CONSIGNEE:
        return consignee;
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER:
        return notifier;
      case BookingTemplateConstants.PARTNER_ADDRESS_TYPE.SHIPPER:
        return shipper;
    }
  };

  const openModal = (
    modalType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => {
    setOpenedModal(modalType);
  };

  const closeModal = () => {
    setOpenedModal(null);
  };

  const handleOpenSpecificModal = (
    modalType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => {
    openModal(modalType);
    if (userProfile) {
      if (
        modalType === BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER
      ) {
        dispatch(getNotifiers(userProfile?.SelectedCompany.PartnerCode));
      } else {
        dispatch(getPartners(userProfile?.SelectedCompany.PartnerCode));
      }
    }
  };

  return (
    <Section>
      <Group onChange={onAddressTypeChange}>
        <CheckForm
          id="party_sender"
          label={t("LABEL_TOGGLE_SHIPPER")}
          type="radio"
          value={AddressType.SHP}
          {...register("BookingPartyType")}
        />

        <CheckForm
          id="party_receiver"
          label={t("LABEL_TOGGLE_CONSIGNEE")}
          type="radio"
          value={AddressType.CEE}
          {...register("BookingPartyType")}
        />
      </Group>
      <Grid mdCol={3}>
        <Section title={t("LABEL_SHIPPER")}>
          <ActionCard
            actionText={t("LABEL_EDIT")}
            isRemovable={bookingPartyType !== AddressType.SHP}
            noDataActionText={t("LABEL_ADD")}
            noDataMessage={t("TEXT_NO_SHIPPER")}
            onActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.SHIPPER
              )
            }
            onNoDataActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.SHIPPER
              )
            }
            onRemoveClick={() =>
              onAddressRemove(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.SHIPPER
              )
            }
            removeText={t("LABEL_REMOVE")}
          >
            {shipper ? <PartnerCardInfo partner={shipper} /> : null}
          </ActionCard>
        </Section>
        <Section title={t("LABEL_CONSIGNEE")}>
          <ActionCard
            actionText={t("LABEL_EDIT")}
            isRemovable={bookingPartyType !== AddressType.CEE}
            noDataActionText={t("LABEL_ADD")}
            noDataMessage={t("TEXT_NO_CONSIGNEE")}
            onActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.CONSIGNEE
              )
            }
            onNoDataActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.CONSIGNEE
              )
            }
            onRemoveClick={() =>
              onAddressRemove(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.CONSIGNEE
              )
            }
            removeText={t("LABEL_REMOVE")}
          >
            {consignee ? <PartnerCardInfo partner={consignee} /> : null}
          </ActionCard>
        </Section>
        <Section title={t("LABEL_NOTIFIER")}>
          <ActionCard
            actionText={t("LABEL_EDIT")}
            isRemovable
            noDataActionText={t("LABEL_ADD")}
            noDataMessage={t("TEXT_NO_NOTIFIER")}
            onActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER
              )
            }
            onNoDataActionClick={() =>
              handleOpenSpecificModal(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER
              )
            }
            onRemoveClick={() =>
              onAddressRemove(
                BookingTemplateConstants.PARTNER_ADDRESS_TYPE.NOTIFIER
              )
            }
            removeText={t("LABEL_REMOVE")}
          >
            {notifier ? <PartnerCardInfo partner={notifier} /> : null}
          </ActionCard>
        </Section>
      </Grid>
      <Divider />
      {/* The modal is rerendered each time it's opened, else the inside form would be one step behind. */}
      {!!openedModal && (
        <PartnerModal
          addressType={openedModal}
          formValues={getPartnerValues(openedModal) || undefined}
          isLoading={isLoading}
          onClose={closeModal}
          onSave={onAddressSave}
          partners={partners}
          title={t(getTitle(openedModal))}
        />
      )}
    </Section>
  );
};

export default AddressesSection;
